import React, { useRef } from 'react';
import Accordion, { HeaderPrimaryItem, HeaderDetailItem } from '../../shared/Accordion';
import { ResourceStrings } from '../../../ResourceStrings';
import styled from 'styled-components/macro';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/rootState';
import { ReportType } from '../../../store/appState';
import { App } from '../../../app';
import * as Actions from '../../../store/actions';
import ImageEditor from '../../shared/ImageEditor';
import { ImageInfo } from '../../../communication/PlatformActions';
import { CropRect } from '../../../CropRect';
import { MessageBarType, MessageBar } from '@fluentui/react/lib/MessageBar';
import { Checkbox } from '@fluentui/react/lib/Checkbox';

export interface ScreenshotEditorProps {
    reportType: ReportType;
    disabled?: boolean;
}

export default function ScreenshotEditor(props: ScreenshotEditorProps): JSX.Element {
    const state = useSelector((state: RootState) => state.appState);
    const refState = useRef(state);
    refState.current = state;

    const includeScreenshotChanged = (): void => {
        const state = refState.current;

        // @TODO: See if still needed
        // Workaround for Riot.JS - checkbox needs to be updated AFTER other updates for screen readers to read it
        // const checkBox: HTMLInputElement = e.target as HTMLInputElement;
        // App.WorkaroundScreenReader(() => {
        //     checkBox.setAttribute('aria-checked', checkBox.checked ? "true" : "false");
        // });

        if (state.reportIncludeScreenshot && state.reportIsCropping) {
            cropCancelClick();
        }

        App.telemetry.postBusinessEvent(App.getEventNameForReportType("includescreenshot-clicked", props.reportType), {
            selected: (!state.reportIncludeScreenshot) ? "Yes" : "No",
        });

        Actions.rapUpdateIncludeScreenshot(!state.reportIncludeScreenshot);
    };

    const cropClick = (): void => {
        Actions.setIsCropping(true);
        Actions.setCropRegion(null, null);

        Actions.setIsCropPromptVisible(true);

        App.telemetry.postBusinessEvent(App.getEventNameForReportType("crop-clicked", props.reportType));
    }

    const cropAcceptClick = (cropRect: CropRect): void => {
        Actions.setIsCropping(false);

        App.telemetry.postBusinessEvent(App.getEventNameForReportType("cropaccept-clicked", props.reportType));

        Actions.setCropRegion(cropRect, null);

        App.read(ResourceStrings.accepted());
    }

    const cropCancelClick = (): void => {
        Actions.setIsCropping(false);
        // @TODO: Implement
        // // Actions.setCropRegion(null, null);
        // // this.refBtnCrop.current!.focus();

        App.telemetry.postBusinessEvent(App.getEventNameForReportType("cropcancel-clicked", props.reportType));

        App.read(ResourceStrings.cancelled());
    }

    const getScreenshotDataUrl = (screenshotThumb: ImageInfo, screenshotThumbUrl: string): string => {
        if (refState.current.reportCroppedScreenshotDataUrl != null) {
            return refState.current.reportCroppedScreenshotDataUrl;
        }

        if (screenshotThumb == null) {
            return null;
        }
        return screenshotThumbUrl;
    }

    return (
        <Accordion
            key={`screenshot-panel-container`}
            data-cy="rap-screenshot-container"
            titleIcon="Attach"
            title={<span css="display: flex">
                <HeaderPrimaryItem>{ResourceStrings.includeScreenshotTitle()}&nbsp;&nbsp;</HeaderPrimaryItem>
                <HeaderDetailItem>Included {state.reportIncludeScreenshot ? "1" : "0"}</HeaderDetailItem>
            </span>}
        >
            <Content>
                <div>
                    <Checkbox
                        checked={state.reportIncludeScreenshot}
                        label="Include this screenshot:"
                        onChange={() => {
                            if (!props.disabled) {
                                includeScreenshotChanged();
                            }
                        }}
                    />
                </div>

                {state.reportIncludeScreenshot && !App.settings.screenshotThumb?.dataUrl && (
                    <MessageBar messageBarType={MessageBarType.error}>
                        {ResourceStrings.includeScreenshotCantGetScreenshot()}
                    </MessageBar>
                )}

                {App.settings.screenshotThumb?.dataUrl && <ImageEditor
                    imagePath={getScreenshotDataUrl(App.settings.screenshotThumb, App.settings.screenshotThumb.dataUrl)}
                    altText={ResourceStrings.previewScreenshotTitle()}
                    disabled={!state.reportIncludeScreenshot}
                    isCropping={state.reportIsCropping}
                    cropRegion={state.reportCropRegion}
                    onCropClicked={() => cropClick()}
                    onAcceptCropClicked={(cropRect: CropRect) => cropAcceptClick(cropRect)}
                    onCancelCropClicked={() => cropCancelClick()}
                />}
            </Content>
        </Accordion>
    );
}

const Content = styled.div`
    padding: 1.4rem 4.8rem 2.8rem 4.8rem;
`;
